import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { TestList, type TestProject } from "@/features/tests/TestList";
import { TestPreviewModal } from "@/features/tests/TestPreviewModal";
import { TestApplyDialog } from "@/features/tests/TestApplyDialog";
import { getTestProjectsByCategory } from "@/features/tests/testData";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";

export const Route = createFileRoute("/app-tests")({
  head: () => ({
    meta: [
      { title: "App Testing — PataTasks" },
      {
        name: "description",
        content:
          "PataTasks is an app and web app testing platform. Browse mobile app testing projects across music, education, browsing, social, communication, productivity, development, and more. Earn money by sharing real user feedback.",
      },
    ],
  }),
  component: AppTestsPage,
});

const APP_SUBCATEGORIES = [
  "All",
  "Social",
  "Education",
  "Tech",
  "E-commerce",
  "Productivity",
  "Others",
];

function AppTestsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [subcategory, setSubcategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState<TestProject | null>(null);
  const [paymentProject, setPaymentProject] = useState<TestProject | null>(null);
  const user = useAppStore((s) => s.user);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const balance = Number((user as any)?.balance ?? 0);
  const maintenancePaid = Number(
    (user as any)?.maintenancePaid ?? (user as any)?.maintenance_paid ?? 0,
  );
  const lockThreshold = Number(
    (user as any)?.lockThreshold ?? (user as any)?.lock_threshold ?? 800,
  );
  const isLocked = balance >= lockThreshold && maintenancePaid === 0 && user?.isAdmin !== 1;
  const lockThresholdUsd = lockThreshold / 100;

  const projects = useMemo(() => {
    let filtered = getTestProjectsByCategory("app-tests");

    if (subcategory && subcategory !== "All") {
      filtered = filtered.filter((p) => p.subcategory.toLowerCase() === subcategory.toLowerCase());
    }

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.appName.toLowerCase().includes(q) ||
          p.instructions.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [search, subcategory]);

  const handleLockedClick = (project: TestProject) => {
    if (!isAuthenticated) {
      window.location.href = "/login";
      return;
    }
    setPaymentProject(project);
  };

  return (
    <div className="bg-background pb-20 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">App Tests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Browse mobile app testing projects across music, education, browsing, social,
            communication, productivity, development, and more
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search app tests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {APP_SUBCATEGORIES.map((cat) => (
            <Button
              key={cat}
              variant={subcategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSubcategory(cat)}
              className="whitespace-nowrap capitalize"
            >
              {subcategory === cat && cat !== "All" && <X className="mr-1 h-3 w-3" />}
              {cat}
            </Button>
          ))}
        </div>

        <TestList
          projects={projects}
          onPreview={setSelectedProject}
          initialVisibleCount={6}
          isLocked={isLocked}
          onLockedClick={handleLockedClick}
        />
      </div>
      <TestPreviewModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onStartTest={() => {
          if (selectedProject) {
            localStorage.setItem("pending_test_id", selectedProject.id);
            navigate({ to: "/submit-test" });
          }
        }}
      />
      <TestApplyDialog
        project={paymentProject!}
        open={!!paymentProject}
        onClose={() => setPaymentProject(null)}
        onSuccess={() => {
          setPaymentProject(null);
          if (paymentProject) {
            localStorage.setItem("pending_test_id", paymentProject.id);
            navigate({ to: "/submit-test" });
          }
        }}
        lockThresholdUsd={lockThresholdUsd}
      />
      <BottomNav />
    </div>
  );
}
